import { useEffect, useRef, useState } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { formatEventTime, formatLocalTimestamp, formatRecurrenceDays } from '@/shared/lib/eventTime';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Skeleton,
  Stack,
  SvgIcon,
  Popover,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import { DjPageShell } from '@/shared/ui/DjPageShell';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { eventApi } from '@/entities/event/api/event.api';
import { spotifyApi } from '@/entities/spotify/api/spotify.api';
import type { SpotifyPlaylist } from '@/entities/spotify/model/spotify.types';
import { requestApi } from '@/entities/request/api/request.api';
import type { SongRequest } from '@/entities/request/model/request.types';
import type { DjEvent, EventRequestStatus } from '@/entities/event/model/event.types';
import { routePaths } from '@/app/router/routePaths';

const iconPaths = {
  back: 'M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z',
  location: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
  calendar: 'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z',
  delete: 'M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z',
  copy: 'M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z',
  check: 'M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z',
  qr: 'M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm10-2h2v2h-2v-2zm4 0h2v4h-4v-2h2v-2zm-6 4h2v4h-2v-4zm4 2h4v2h-4v-2z',
  spotify: 'M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm4.586 14.424a.623.623 0 0 1-.857.207c-2.348-1.435-5.304-1.76-8.785-.964a.623.623 0 1 1-.277-1.215c3.809-.87 7.077-.496 9.712 1.115a.623.623 0 0 1 .207.857zm1.223-2.722a.78.78 0 0 1-1.072.257c-2.687-1.652-6.785-2.131-9.965-1.166a.78.78 0 0 1-.973-.519.78.78 0 0 1 .518-.973c3.632-1.102 8.147-.568 11.234 1.329a.78.78 0 0 1 .258 1.072zm.105-2.835C14.692 8.95 9.375 8.775 6.297 9.71a.937.937 0 1 1-.543-1.795c3.532-1.07 9.404-.862 13.115 1.338a.936.936 0 0 1 .045 1.614z',
  play: 'M8 5v14l11-7z',
  pause: 'M6 19h4V5H6v14zm8-14v14h4V5h-4z',
  close: 'M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z',
  wave: 'M4 10h2v8H4v-8zm4-4h2v12H8V6zm4-3h2v15h-2V3zm4 6h2v9h-2V9zm4-4h2v13h-2V5z',
  link: 'M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z',
  music: 'M12 3v11.55A4 4 0 1 0 14 18V7h6V3h-8zM8 20a2 2 0 1 1 0-4 2 2 0 0 1 0 4z',
  info: 'M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z',
  edit: 'M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z',
  externalLink: 'M19 19H5V5h7V3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z',
  share: 'M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z',
};

const PLAYLIST_GRADIENTS = [
  'linear-gradient(135deg,#1DB954,#0d7a36)',
  'linear-gradient(135deg,#6C63FF,#3b35cc)',
  'linear-gradient(135deg,#FF6B6B,#c0392b)',
  'linear-gradient(135deg,#F59E0B,#b45309)',
  'linear-gradient(135deg,#00E5FF,#007a99)',
];

const SPOTIFY_GREEN = '#1DB954';

const statusMeta = {
  ACTIVE:   { label: 'Active',   color: '#10B981',             bg: 'rgba(16,185,129,0.13)',  border: 'rgba(16,185,129,0.3)'  },
  PENDING:  { label: 'Pending',  color: '#818CF8',             bg: 'rgba(129,140,248,0.13)', border: 'rgba(129,140,248,0.3)' },
  CLOSED:   { label: 'Closed',   color: '#F59E0B',             bg: 'rgba(245,158,11,0.13)',  border: 'rgba(245,158,11,0.3)'  },
  ARCHIVED: { label: 'Archived', color: 'rgba(255,255,255,0.3)', bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.1)' },
};

const reqStatusMeta: Record<EventRequestStatus, { label: string; dot: string }> = {
  OPEN: { label: 'Requests open', dot: '#10B981' },
  PAUSED: { label: 'Requests paused', dot: '#F59E0B' },
  CLOSED: { label: 'Requests closed', dot: '#EF4444' },
};

function AppIcon({ path, ...props }: SvgIconProps & { path: string }) {
  return <SvgIcon {...props}><path d={path} /></SvgIcon>;
}

function GlassCard({ children, sx = {} }: { children: React.ReactNode; sx?: object }) {
  const theme = useTheme();
  return (
    <Box sx={{
      position: 'relative',
      overflow: 'hidden',
      p: { xs: 2, sm: 2.5 },
      borderRadius: 2,
      border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
      bgcolor: alpha(theme.palette.common.white, 0.065),
      backdropFilter: 'blur(20px)',
      boxShadow: `0 20px 46px ${alpha(theme.palette.common.black, 0.16)}`,
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: `linear-gradient(90deg, ${theme.palette.warning.light}, ${theme.palette.secondary.light}, ${theme.palette.primary.light})`,
        opacity: 0.75,
      },
      ...sx,
    }}>
      {children}
    </Box>
  );
}

function CardLabel({ icon, children }: { icon: string; children: string }) {
  const theme = useTheme();
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 2 }}>
      <AppIcon path={icon} sx={{ fontSize: 15, color: alpha(theme.palette.common.white, 0.3) }} />
      <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: alpha(theme.palette.common.white, 0.4) }}>
        {children}
      </Typography>
    </Stack>
  );
}

function formatDate(d?: string) {
  if (!d) return null;
  return new Date(d + 'T00:00:00').toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
}


// ── Request status toggle ──────────────────────────────────────────────────

function RequestStatusToggle({ value, onChange, updating }: {
  value: EventRequestStatus;
  onChange: (v: EventRequestStatus) => void;
  updating: boolean;
}) {
  const theme = useTheme();
  const options: { key: EventRequestStatus; label: string; dot: string }[] = [
    { key: 'OPEN', label: 'Open', dot: '#10B981' },
    { key: 'PAUSED', label: 'Paused', dot: '#F59E0B' },
  ];
  return (
    <Stack direction="row" spacing={0.5} sx={{ p: 0.5, borderRadius: 99, border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`, bgcolor: alpha(theme.palette.common.white, 0.06), width: 'fit-content' }}>
      {options.map((opt) => {
        const active = value === opt.key;
        return (
          <Button
            key={opt.key}
            size="small"
            disabled={updating}
            onClick={() => !active && onChange(opt.key)}
            sx={{
              px: 1.5,
              py: 0.625,
              minWidth: 0,
              fontSize: 13,
              fontWeight: active ? 900 : 600,
              color: active ? 'common.white' : alpha(theme.palette.common.white, 0.45),
              bgcolor: active ? alpha(theme.palette.common.white, 0.12) : 'transparent',
              borderRadius: 99,
              gap: 0.75,
              transition: 'all 0.15s',
              ...(active && { boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.common.white, 0.15)}` }),
              '&:hover': { color: 'common.white', bgcolor: alpha(theme.palette.common.white, 0.1) },
            }}
          >
            {updating && active
              ? <CircularProgress size={10} sx={{ color: 'inherit' }} />
              : <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: active ? opt.dot : alpha(theme.palette.common.white, 0.2), flexShrink: 0, transition: 'background 0.15s' }} />
            }
            {opt.label}
          </Button>
        );
      })}
    </Stack>
  );
}


// ── QR / Share panel ───────────────────────────────────────────────────────

function QrPanel({ event }: { event: DjEvent }) {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  const guestUrl = `${window.location.origin}/events/${event.eventCode}`;

  const copy = async () => {
    await navigator.clipboard.writeText(guestUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <GlassCard>
      <CardLabel icon={iconPaths.qr}>Share event</CardLabel>

      {event.qrCodeUrl ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Box
            component="img"
            src={event.qrCodeUrl}
            alt="Event QR code"
            sx={{ width: 164, height: 164, borderRadius: 2, border: `1px solid ${alpha(theme.palette.common.white, 0.14)}`, boxShadow: `0 18px 44px ${alpha(theme.palette.common.black, 0.24)}` }}
          />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Box sx={{ width: 164, height: 164, borderRadius: 2, border: `2px dashed ${alpha(theme.palette.common.white, 0.14)}`, display: 'grid', placeItems: 'center', bgcolor: alpha(theme.palette.common.white, 0.035) }}>
            <Stack alignItems="center" spacing={1}>
              <AppIcon path={iconPaths.qr} sx={{ fontSize: 38, color: alpha(theme.palette.common.white, 0.18) }} />
              <Typography sx={{ fontSize: 11, color: alpha(theme.palette.common.white, 0.3), textAlign: 'center', px: 1 }}>
                QR code generated when event goes live
              </Typography>
            </Stack>
          </Box>
        </Box>
      )}

      {/* Event code */}
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: alpha(theme.palette.common.white, 0.35), letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            Event code
          </Typography>
          <Chip
            label={event.eventCode}
            size="small"
            sx={{
              height: 22,
              fontFamily: 'monospace',
              fontSize: 12,
              fontWeight: 700,
              color: alpha(theme.palette.common.white, 0.75),
              bgcolor: alpha(theme.palette.common.white, 0.08),
              border: `1px solid ${alpha(theme.palette.common.white, 0.14)}`,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        </Stack>

        {/* Guest link */}
        <Box sx={{ px: 1.5, py: 1.25, borderRadius: 1.5, bgcolor: alpha(theme.palette.common.white, 0.055), border: `1px solid ${alpha(theme.palette.common.white, 0.1)}` }}>
          <Typography noWrap sx={{ fontSize: 12, color: alpha(theme.palette.common.white, 0.45), fontFamily: 'monospace' }}>
            {guestUrl}
          </Typography>
        </Box>

        <Tooltip title={copied ? 'Copied!' : 'Copy guest link'} placement="top">
          <Button
            onClick={copy}
            variant="outlined"
            fullWidth
            size="small"
            startIcon={<AppIcon path={copied ? iconPaths.check : iconPaths.copy} sx={{ fontSize: '16px !important' }} />}
            sx={{
              color: copied ? '#10B981' : alpha(theme.palette.common.white, 0.65),
              borderColor: copied ? 'rgba(16,185,129,0.4)' : alpha(theme.palette.common.white, 0.18),
              bgcolor: copied ? 'rgba(16,185,129,0.08)' : 'transparent',
              fontSize: 13,
              fontWeight: 700,
              borderRadius: 1.5,
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: alpha(theme.palette.common.white, 0.35),
                bgcolor: alpha(theme.palette.common.white, 0.07),
                color: 'common.white',
              },
            }}
          >
            {copied ? 'Link copied!' : 'Copy guest link'}
          </Button>
        </Tooltip>
      </Stack>
    </GlassCard>
  );
}

// ── Spotify panel ──────────────────────────────────────────────────────────

function SpotifyPanel({ event, onNavigate, onPlaylistChange, onShareToggle, canEdit }: {
  event: DjEvent;
  onNavigate: () => void;
  onPlaylistChange: (id: string, name: string) => Promise<void>;
  onShareToggle: (shared: boolean) => Promise<void>;
  canEdit: boolean;
}) {
  const theme = useTheme();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [playlists, setPlaylists] = useState<SpotifyPlaylist[]>([]);
  const [loadingPlaylists, setLoadingPlaylists] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sharingUpdating, setSharingUpdating] = useState(false);

  const handleShareToggle = async () => {
    setSharingUpdating(true);
    try {
      await onShareToggle(!event.sharePlaylist);
    } finally {
      setSharingUpdating(false);
    }
  };

  const openPicker = () => {
    setPickerOpen(true);
    if (playlists.length === 0) {
      setLoadingPlaylists(true);
      spotifyApi.getPlaylists()
        .then(setPlaylists)
        .finally(() => setLoadingPlaylists(false));
    }
  };

  const handleSelect = async (pl: SpotifyPlaylist) => {
    setSaving(true);
    try {
      await onPlaylistChange(pl.playlistId, pl.name);
      setPickerOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <GlassCard>
      <CardLabel icon={iconPaths.spotify}>Playlist</CardLabel>
      {event.spotifyPlaylistId ? (
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Tooltip title="Open in Spotify">
              <Box
                component="a"
                href={`https://open.spotify.com/playlist/${event.spotifyPlaylistId}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ width: 44, height: 44, borderRadius: '34% 46% 36% 44%', bgcolor: alpha(SPOTIFY_GREEN, 0.14), border: `1px solid ${alpha(SPOTIFY_GREEN, 0.25)}`, display: 'grid', placeItems: 'center', flexShrink: 0, transform: 'rotate(-4deg)', cursor: 'pointer', transition: 'opacity 0.15s', '&:hover': { opacity: 0.8 } }}
              >
                <AppIcon path={iconPaths.spotify} sx={{ fontSize: 22, color: SPOTIFY_GREEN }} />
              </Box>
            </Tooltip>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography noWrap sx={{ fontSize: 14, fontWeight: 800, color: 'common.white' }}>
                {event.spotifyPlaylistName ?? 'Linked playlist'}
              </Typography>
              <Typography sx={{ fontSize: 12, color: alpha(theme.palette.common.white, 0.45) }}>Connected via Spotify</Typography>
            </Box>
            <Stack direction="column" sx={{ flexShrink: 0 }}>
              <Tooltip title={pickerOpen ? 'Cancel' : canEdit ? 'Change playlist' : 'Pause or stop playing first'}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!canEdit && !pickerOpen}
                    onClick={() => pickerOpen ? setPickerOpen(false) : openPicker()}
                    sx={{ color: pickerOpen ? alpha(theme.palette.common.white, 0.5) : SPOTIFY_GREEN, p: 0.5, '&.Mui-disabled': { color: alpha(theme.palette.common.white, 0.2) } }}
                  >
                    <AppIcon path={pickerOpen ? iconPaths.close : iconPaths.edit} sx={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Share playlist toggle */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pt: 1, borderTop: `1px solid ${alpha(theme.palette.common.white, 0.07)}` }}>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <AppIcon path={iconPaths.share} sx={{ fontSize: 14, color: alpha(theme.palette.common.white, 0.3) }} />
              <Typography sx={{ fontSize: 12.5, color: alpha(theme.palette.common.white, 0.45), fontWeight: 600 }}>
                Share playlist with guests
              </Typography>
            </Stack>
            <Box
              onClick={() => !sharingUpdating && handleShareToggle()}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                px: 1, py: 0.375,
                borderRadius: 99,
                cursor: sharingUpdating ? 'wait' : 'pointer',
                color: event.sharePlaylist ? SPOTIFY_GREEN : alpha(theme.palette.common.white, 0.35),
                bgcolor: event.sharePlaylist ? alpha(SPOTIFY_GREEN, 0.1) : alpha(theme.palette.common.white, 0.06),
                border: `1px solid ${event.sharePlaylist ? alpha(SPOTIFY_GREEN, 0.3) : alpha(theme.palette.common.white, 0.1)}`,
                transition: 'all 0.2s',
                '&:hover': { opacity: sharingUpdating ? 1 : 0.8 },
              }}
            >
              {sharingUpdating
                ? <CircularProgress size={10} sx={{ color: 'inherit' }} />
                : null}
              <Typography sx={{ fontSize: 11.5, fontWeight: 800, lineHeight: 1 }}>
                {event.sharePlaylist ? 'Shared' : 'Private'}
              </Typography>
            </Box>
          </Stack>

          {pickerOpen && (
            <Box sx={{ borderTop: `1px solid ${alpha(theme.palette.common.white, 0.08)}`, pt: 1.5 }}>
              {loadingPlaylists ? (
                <Stack spacing={0.75}>
                  {[...Array(3)].map((_, i) => <Skeleton key={i} variant="rounded" height={44} sx={{ bgcolor: alpha(theme.palette.common.white, 0.06), borderRadius: 1.25 }} />)}
                </Stack>
              ) : playlists.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: alpha(theme.palette.common.white, 0.35), textAlign: 'center', py: 1 }}>
                  No playlists found on your Spotify account.
                </Typography>
              ) : (
                <Stack spacing={0.5} sx={{ maxHeight: 240, overflowY: 'auto' }}>
                  {playlists.map((pl, i) => {
                    const isLinked = pl.playlistId === event.spotifyPlaylistId;
                    return (
                      <Box
                        key={pl.playlistId}
                        onClick={() => !saving && handleSelect(pl)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1.25, p: 1, borderRadius: 1.25,
                          border: `1px solid ${isLinked ? alpha(SPOTIFY_GREEN, 0.4) : alpha(theme.palette.common.white, 0.08)}`,
                          bgcolor: isLinked ? alpha(SPOTIFY_GREEN, 0.1) : alpha(theme.palette.common.white, 0.04),
                          cursor: saving ? 'wait' : 'pointer',
                          transition: 'all 0.15s',
                          '&:hover': { bgcolor: isLinked ? alpha(SPOTIFY_GREEN, 0.16) : alpha(theme.palette.common.white, 0.08) },
                        }}
                      >
                        {pl.imageUrl ? (
                          <Box component="img" src={pl.imageUrl} sx={{ width: 32, height: 32, borderRadius: 1, objectFit: 'cover', flexShrink: 0 }} />
                        ) : (
                          <Box sx={{ width: 32, height: 32, borderRadius: 1, background: PLAYLIST_GRADIENTS[i % PLAYLIST_GRADIENTS.length], display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                            <AppIcon path={iconPaths.spotify} sx={{ fontSize: 14, color: '#fff' }} />
                          </Box>
                        )}
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography noWrap sx={{ fontSize: 12, fontWeight: 700, color: isLinked ? SPOTIFY_GREEN : 'common.white' }}>
                            {pl.name}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: alpha(theme.palette.common.white, 0.35) }}>
                            {pl.trackCount} tracks
                          </Typography>
                        </Box>
                        {isLinked && (
                          saving
                            ? <CircularProgress size={12} sx={{ color: SPOTIFY_GREEN, flexShrink: 0 }} />
                            : <Chip label="Current" size="small" sx={{ height: 16, fontSize: 9, fontWeight: 800, color: SPOTIFY_GREEN, bgcolor: alpha(SPOTIFY_GREEN, 0.15), border: `1px solid ${alpha(SPOTIFY_GREEN, 0.3)}`, '& .MuiChip-label': { px: 0.6 }, flexShrink: 0 }} />
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>
          )}
        </Stack>
      ) : (
        <Stack spacing={1.5} alignItems="center" sx={{ py: 1 }}>
          <AppIcon path={iconPaths.spotify} sx={{ fontSize: 32, color: alpha(theme.palette.common.white, 0.18) }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: alpha(theme.palette.common.white, 0.55) }}>No playlist linked</Typography>
            <Typography sx={{ fontSize: 12, color: alpha(theme.palette.common.white, 0.35), mt: 0.35 }}>Connect Spotify to power this event's queue.</Typography>
          </Box>
          <Button
            onClick={onNavigate}
            size="small"
            variant="outlined"
            startIcon={<AppIcon path={iconPaths.link} />}
            sx={{
              color: SPOTIFY_GREEN,
              borderColor: alpha(SPOTIFY_GREEN, 0.35),
              fontSize: 13,
              fontWeight: 700,
              borderRadius: 1.5,
              '&:hover': { bgcolor: alpha(SPOTIFY_GREEN, 0.08), borderColor: SPOTIFY_GREEN },
            }}
          >
            Link Spotify
          </Button>
        </Stack>
      )}
    </GlassCard>
  );
}

// ── Status chip colours ─────────────────────────────────────────────────────

const reqChipColor: Record<string, { color: string; bg: string; border: string }> = {
  PENDING:   { color: '#F59E0B', bg: 'rgba(245,158,11,0.13)',  border: 'rgba(245,158,11,0.3)'  },
  APPROVED:  { color: '#10B981', bg: 'rgba(16,185,129,0.13)',  border: 'rgba(16,185,129,0.3)'  },
  REJECTED:  { color: '#EF4444', bg: 'rgba(239,68,68,0.13)',   border: 'rgba(239,68,68,0.3)'   },
  PLAYED:    { color: '#00E5FF', bg: 'rgba(0,229,255,0.10)',   border: 'rgba(0,229,255,0.25)'  },
  CANCELLED: { color: 'rgba(255,255,255,0.3)', bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.1)' },
};

// ── Request queue ──────────────────────────────────────────────────────────

function RequestQueue({ event, requestStatus, onStatusChange, statusUpdating, spotifyPlaying }: {
  event: DjEvent;
  requestStatus: EventRequestStatus;
  onStatusChange: (v: EventRequestStatus) => void;
  statusUpdating: boolean;
  spotifyPlaying: boolean | null;
}) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [acting, setActing] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);
  const [embedAnchor, setEmbedAnchor] = useState<HTMLElement | null>(null);
  const [embedTrackId, setEmbedTrackId] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const handlePreview = (req: SongRequest, anchor: HTMLElement) => {
    if (req.previewUrl) {
      if (playingUrl === req.previewUrl) {
        audioRef.current?.pause();
        setPlayingUrl(null);
      } else {
        audioRef.current?.pause();
        audioRef.current = new Audio(req.previewUrl);
        audioRef.current.play();
        audioRef.current.onended = () => setPlayingUrl(null);
        setPlayingUrl(req.previewUrl);
      }
    } else {
      if (embedTrackId === req.spotifyTrackId) {
        setEmbedAnchor(null);
        setEmbedTrackId(null);
      } else {
        setEmbedTrackId(req.spotifyTrackId!);
        setEmbedAnchor(anchor);
      }
    }
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: loading,
  } = useInfiniteQuery({
    queryKey: ['dj-requests', event.eventId],
    queryFn: ({ pageParam }) => requestApi.getDjRequests(event.eventId, { limit: 20, cursor: pageParam }),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? null,
    initialPageParam: undefined as string | undefined,
    refetchInterval: 15000,
    refetchIntervalInBackground: false,
  });

  const requests = data?.pages.flatMap((p) => p.items) ?? [];

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage(); },
      { threshold: 0.1 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const act = async (id: string, fn: () => Promise<SongRequest>) => {
    setActing(id);
    try {
      const updated = await fn();
      queryClient.setQueryData(
        ['dj-requests', event.eventId],
        (prev: { pages: { items: SongRequest[]; nextCursor?: string }[]; pageParams: unknown[] } | undefined) => {
          if (!prev) return prev;
          return { ...prev, pages: prev.pages.map((p) => ({ ...p, items: p.items.map((r) => r.requestId === id ? updated : r) })) };
        },
      );
    } finally {
      setActing(null);
    }
  };

  return (
    <>
    <GlassCard>
      <Stack sx={{ mb: 2.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CardLabel icon={iconPaths.wave}>Song Requests</CardLabel>
            {requests.length > 0 && (
              <Chip label={requests.length} size="small" sx={{ height: 18, fontSize: 11, fontWeight: 800, color: alpha(theme.palette.common.white, 0.6), bgcolor: alpha(theme.palette.common.white, 0.1), '& .MuiChip-label': { px: 0.75 } }} />
            )}
          </Stack>
          {event.status === 'ACTIVE' && (
            <RequestStatusToggle value={requestStatus} onChange={onStatusChange} updating={statusUpdating} />
          )}
        </Stack>
        {event.status === 'ACTIVE' && spotifyPlaying === false && (
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#F59E0B', flexShrink: 0 }} />
            <Typography sx={{ fontSize: 11, color: alpha(theme.palette.common.white, 0.35) }}>
              Nothing playing on Spotify · guests see requests as paused
            </Typography>
          </Stack>
        )}
      </Stack>

      {loading ? (
        <Stack spacing={1}>
          {[...Array(3)].map((_, i) => <Skeleton key={i} variant="rounded" height={70} sx={{ bgcolor: alpha(theme.palette.common.white, 0.06), borderRadius: 1.5 }} />)}
        </Stack>
      ) : requests.length === 0 ? (
        <Stack alignItems="center" spacing={1.5} sx={{ py: { xs: 3, sm: 5 } }}>
          <Box sx={{ width: 52, height: 52, borderRadius: '34% 46% 36% 44%', bgcolor: alpha(theme.palette.common.white, 0.055), border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`, display: 'grid', placeItems: 'center', transform: 'rotate(-4deg)' }}>
            <AppIcon path={iconPaths.music} sx={{ fontSize: 24, color: alpha(theme.palette.common.white, 0.22) }} />
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: 16, fontWeight: 800, color: alpha(theme.palette.common.white, 0.55) }}>No requests yet</Typography>
            <Typography sx={{ mt: 0.5, fontSize: 13.5, color: alpha(theme.palette.common.white, 0.35) }}>
              {spotifyPlaying === false ? 'Nothing playing on Spotify · start the playlist to accept requests.' : requestStatus === 'OPEN' ? 'Share the event link — requests will appear here.' : requestStatus === 'PAUSED' ? 'Requests are paused.' : 'Requests are closed.'}
            </Typography>
          </Box>
        </Stack>
      ) : (
        <Stack spacing={1}>
          {requests.map((req) => {
            const chip = reqChipColor[req.status] ?? reqChipColor.CANCELLED;
            const isActing = acting === req.requestId;
            const explicitBadge = req.explicit ? (
              <Tooltip title="Explicit track">
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 0.9,
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                    color: '#FFFFFF',
                    bgcolor: 'rgba(220,38,38,0.34)',
                    border: '1px solid rgba(248,113,113,0.9)',
                    boxShadow: '0 0 0 2px rgba(239,68,68,0.12)',
                  }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 950, lineHeight: 1 }}>E</Typography>
                </Box>
              </Tooltip>
            ) : null;
            const requesterBlock = (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '28px minmax(0, 1fr)',
                  alignItems: 'center',
                  columnGap: 0.75,
                  minWidth: 0,
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    flexShrink: 0,
                    overflow: 'hidden',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: alpha(theme.palette.secondary.main, 0.24),
                    border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
                  }}
                >
                  {req.requestedByAvatarUrl ? (
                    <Box component="img" src={req.requestedByAvatarUrl} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <Typography sx={{ fontSize: 10, fontWeight: 900, color: 'secondary.light', lineHeight: 1 }}>
                      {(req.requestedByDisplayName ?? req.requestedByEmail ?? 'G')
                        .split(' ').slice(0, 2).map((w: string) => w[0]?.toUpperCase()).join('')}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 850, color: alpha(theme.palette.common.white, 0.62), lineHeight: 1.15 }}>
                    {req.requestedByDisplayName ?? req.requestedByEmail ?? 'Guest request'}
                  </Typography>
                  <Typography sx={{ mt: 0.25, fontSize: 11.5, fontWeight: 800, color: alpha(theme.palette.warning.light, 0.78), lineHeight: 1.15 }}>
                    Requested {formatLocalTimestamp(req.createdAt)}
                  </Typography>
                </Box>
              </Box>
            );
            // Rail shows while the keep/remove decision is still actionable:
            // pre-play always, post-play/skip only until kept or removed
            const hasKeepAction = !!req.addedToPlaylistByApp && !req.removedFromPlaylistAt && (
              req.status === 'APPROVED' ||
              ((req.status === 'PLAYED' || req.status === 'SKIPPED') && !req.keepInPlaylist)
            );
            const pendingActionRail = req.status === 'PENDING' ? (
              <Tooltip title={spotifyPlaying === false ? 'Start the playlist to approve or reject' : ''} placement="top">
                <Stack
                  spacing={{ xs: 0.7, md: 0.55 }}
                  sx={{
                    width: '100%',
                    height: { md: '100%' },
                    p: { xs: 0, md: 0.6 },
                    borderRadius: { md: 1.5 },
                    bgcolor: { md: alpha(theme.palette.common.white, 0.035) },
                    border: { md: `1px solid ${alpha(theme.palette.common.white, 0.08)}` },
                    justifyContent: { md: 'center' },
                  }}
                >
                  <Stack direction={{ xs: 'row', md: 'column' }} spacing={{ xs: 0.65, md: 0.55 }} sx={{ width: '100%' }}>
                    <Button
                      size="small"
                      disabled={isActing || spotifyPlaying === false}
                      onClick={() => act(req.requestId, () => requestApi.approveRequest(req.requestId))}
                      startIcon={!isActing ? <AppIcon path={iconPaths.check} sx={{ fontSize: '15px !important' }} /> : undefined}
                      sx={{
                        flex: 1,
                        minHeight: { xs: 36, md: 34 },
                        fontSize: 12,
                        fontWeight: 950,
                        color: '#FFFFFF',
                        border: '1px solid #10B981',
                        minWidth: 0,
                        px: 1,
                        borderRadius: 1.2,
                        bgcolor: '#047857',
                        textTransform: 'none',
                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18)',
                        '&:hover': { bgcolor: '#065F46', borderColor: '#34D399', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2)' },
                        '&:active': { transform: 'translateY(1px)' },
                        '&.Mui-disabled': { color: 'rgba(255,255,255,0.36)', bgcolor: '#064E3B', borderColor: '#065F46', boxShadow: 'none' },
                        '& .MuiButton-startIcon': { mr: 0.55 },
                      }}
                    >
                      {isActing ? <CircularProgress size={12} sx={{ color: 'inherit' }} /> : 'Approve'}
                    </Button>
                    <Button
                      size="small"
                      disabled={isActing || spotifyPlaying === false}
                      onClick={() => act(req.requestId, () => requestApi.rejectRequest(req.requestId))}
                      startIcon={<AppIcon path={iconPaths.close} sx={{ fontSize: '14px !important' }} />}
                      sx={{
                        flex: 1,
                        minHeight: { xs: 36, md: 34 },
                        fontSize: 12,
                        fontWeight: 950,
                        color: '#FFFFFF',
                        border: '1px solid #F87171',
                        minWidth: 0,
                        px: 1,
                        borderRadius: 1.2,
                        bgcolor: '#B91C1C',
                        textTransform: 'none',
                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.16)',
                        '&:hover': { bgcolor: '#991B1B', borderColor: '#FCA5A5', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18)' },
                        '&:active': { transform: 'translateY(1px)' },
                        '&.Mui-disabled': { color: 'rgba(255,255,255,0.36)', borderColor: '#7F1D1D', bgcolor: '#7F1D1D', boxShadow: 'none' },
                        '& .MuiButton-startIcon': { mr: 0.55 },
                      }}
                    >
                      Reject
                    </Button>
                  </Stack>
                </Stack>
              </Tooltip>
            ) : null;
            const isKept = !!req.keepInPlaylist;
            const keepActionRail = hasKeepAction ? (
              <Tooltip title="Requested songs are removed from your playlist after they play (the default). Choose Keep track to leave this one in permanently." placement="top">
                <Stack
                  spacing={{ xs: 0.7, md: 0.55 }}
                  justifyContent={{ md: 'center' }}
                  sx={{ width: '100%', height: { md: '100%' }, p: { xs: 0, md: 0.6 }, borderRadius: { md: 1.5 }, bgcolor: { md: alpha(theme.palette.common.white, 0.035) }, border: { md: `1px solid ${alpha(theme.palette.common.white, 0.08)}` } }}
                >
                  <Stack direction={{ xs: 'row', md: 'column' }} spacing={{ xs: 0.65, md: 0.55 }} sx={{ width: '100%' }}>
                    <Button
                      size="small"
                      disabled={isActing}
                      onClick={() => !isKept && act(req.requestId, () => requestApi.keepRequest(req.requestId, true))}
                      startIcon={!(isActing && !isKept) ? <AppIcon path={iconPaths.check} sx={{ fontSize: '15px !important' }} /> : undefined}
                      sx={{
                        flex: 1, minHeight: { xs: 36, md: 34 }, minWidth: 0, px: 1,
                        fontSize: 12, fontWeight: 950, borderRadius: 1.2, textTransform: 'none',
                        color: isKept ? '#FFFFFF' : alpha('#34D399', 0.75),
                        border: `1px solid ${isKept ? '#10B981' : alpha('#10B981', 0.35)}`,
                        bgcolor: isKept ? '#047857' : 'transparent',
                        boxShadow: isKept ? 'inset 0 1px 0 rgba(255,255,255,0.18)' : 'none',
                        '&:hover': { bgcolor: isKept ? '#065F46' : alpha('#10B981', 0.12), borderColor: '#34D399' },
                        '&:active': { transform: 'translateY(1px)' },
                        '&.Mui-disabled': { color: 'rgba(255,255,255,0.36)', borderColor: alpha('#10B981', 0.25), boxShadow: 'none' },
                        '& .MuiButton-startIcon': { mr: 0.55 },
                      }}
                    >
                      {isActing && !isKept ? <CircularProgress size={12} sx={{ color: 'inherit' }} /> : 'Keep track'}
                    </Button>
                    <Button
                      size="small"
                      disabled={isActing}
                      onClick={() => isKept && act(req.requestId, () => requestApi.keepRequest(req.requestId, false))}
                      startIcon={!(isActing && isKept) ? <AppIcon path={iconPaths.close} sx={{ fontSize: '14px !important' }} /> : undefined}
                      sx={{
                        flex: 1, minHeight: { xs: 36, md: 34 }, minWidth: 0, px: 1,
                        fontSize: 12, fontWeight: 950, borderRadius: 1.2, textTransform: 'none',
                        color: !isKept ? '#FFFFFF' : alpha('#FBBF24', 0.75),
                        border: `1px solid ${!isKept ? '#F59E0B' : alpha('#F59E0B', 0.35)}`,
                        bgcolor: !isKept ? '#B45309' : 'transparent',
                        boxShadow: !isKept ? 'inset 0 1px 0 rgba(255,255,255,0.16)' : 'none',
                        '&:hover': { bgcolor: !isKept ? '#92400E' : alpha('#F59E0B', 0.12), borderColor: '#FBBF24' },
                        '&:active': { transform: 'translateY(1px)' },
                        '&.Mui-disabled': { color: 'rgba(255,255,255,0.36)', borderColor: alpha('#F59E0B', 0.25), boxShadow: 'none' },
                        '& .MuiButton-startIcon': { mr: 0.55 },
                      }}
                    >
                      {isActing && isKept ? <CircularProgress size={12} sx={{ color: 'inherit' }} /> : 'Remove track'}
                    </Button>
                  </Stack>
                  <Typography sx={{ fontSize: 10, textAlign: 'center', color: alpha(theme.palette.common.white, 0.35), lineHeight: 1.2 }}>
                    {isKept ? 'Kept in your playlist' : 'Removed after play · default'}
                  </Typography>
                </Stack>
              </Tooltip>
            ) : null;
            const actionRail = pendingActionRail ?? keepActionRail;
            const hasActionRail = !!actionRail;
            return (
              <Box
                key={req.requestId}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: hasActionRail
                      ? 'minmax(0, 1fr) 158px'
                      : '1fr',
                  },
                  alignItems: { xs: 'start', md: hasActionRail ? 'stretch' : 'start' },
                  gap: { xs: 0, md: 1 },
                }}
              >
              <Box
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  p: { xs: 1.2, sm: 1.35 },
                  pl: 1.75,
                  borderRadius: 1.25,
                  border: `1px solid ${alpha(theme.palette.common.white, 0.09)}`,
                  bgcolor: alpha(theme.palette.common.white, 0.052),
                  height: { md: req.status === 'PENDING' ? '100%' : 'auto' },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    background:
                      req.status === 'PENDING'
                        ? `linear-gradient(180deg, ${theme.palette.warning.light}, ${theme.palette.primary.light})`
                        : req.status === 'APPROVED'
                          ? `linear-gradient(180deg, ${theme.palette.success.light}, ${theme.palette.secondary.light})`
                          : req.status === 'REJECTED'
                            ? 'rgba(239,68,68,0.7)'
                            : alpha(theme.palette.common.white, 0.18),
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: 'minmax(0, 1fr) 190px',
                    },
                    alignItems: { xs: 'stretch', md: req.status === 'PENDING' ? 'start' : 'center' },
                    gap: { xs: 1, md: 1.25 },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '32px minmax(0, 1fr)',
                          sm: '32px minmax(210px, 280px) 82px',
                          md: '32px minmax(230px, 320px) 82px',
                        },
                        alignItems: 'center',
                        columnGap: { xs: 0.8, sm: 0.75 },
                        rowGap: 0.65,
                        minWidth: 0,
                      }}
                    >
                      {req.spotifyTrackId && (() => {
                        const isEmbed = embedTrackId === req.spotifyTrackId;
                        const isAudio = !!req.previewUrl && playingUrl === req.previewUrl;
                        const active = isEmbed || isAudio;
                        return (
                          <Tooltip title="Preview" placement="top">
                            <IconButton
                              size="small"
                              onClick={(e) => handlePreview(req, e.currentTarget)}
                              sx={{
                                flexShrink: 0,
                                width: 32,
                                height: 32,
                                color: active ? theme.palette.secondary.light : alpha(theme.palette.common.white, 0.58),
                                bgcolor: active ? alpha(theme.palette.secondary.main, 0.22) : alpha(theme.palette.common.white, 0.06),
                                border: `1px solid ${active ? alpha(theme.palette.secondary.light, 0.44) : alpha(theme.palette.common.white, 0.12)}`,
                                '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.16), color: theme.palette.secondary.light },
                              }}
                            >
                              <AppIcon path={isAudio ? iconPaths.pause : isEmbed ? iconPaths.close : iconPaths.play} sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        );
                      })()}
                      {!req.spotifyTrackId && <Box sx={{ width: 32, height: 32 }} />}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: { xs: 16.5, sm: 17.5 }, fontWeight: 950, color: 'common.white', lineHeight: 1.12 }}>
                          {req.trackName}
                        </Typography>
                        <Typography noWrap sx={{ mt: 0.3, fontSize: { xs: 13.25, sm: 13.75 }, fontWeight: 760, color: alpha(theme.palette.common.white, 0.68), lineHeight: 1.2 }}>
                          {req.artistName}
                          {req.albumName && (
                            <Box component="span" sx={{ color: alpha(theme.palette.common.white, 0.38), fontWeight: 500 }}> · {req.albumName}</Box>
                          )}
                        </Typography>
                      </Box>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.45}
                        sx={{
                          gridColumn: { xs: '2 / 3', sm: 'auto' },
                          justifySelf: { xs: 'start', sm: 'start' },
                          flexShrink: 0,
                        }}
                      >
                        <Chip
                          label={req.status === 'PLAYED' && req.playedAt
                            ? `Played ${new Date(req.playedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                            : req.status}
                          size="small"
                          sx={{ height: 22, fontSize: 11, fontWeight: 900, flexShrink: 0, color: chip.color, bgcolor: chip.bg, border: `1px solid ${chip.border}`, '& .MuiChip-label': { px: 0.65 } }}
                        />
                        {req.removedFromPlaylistAt && (
                          <Tooltip title={`Removed from your playlist ${new Date(req.removedFromPlaylistAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`} placement="top" arrow>
                            <Chip
                              label="Removed"
                              size="small"
                              sx={{ height: 22, fontSize: 11, fontWeight: 900, flexShrink: 0, color: '#FBBF24', bgcolor: 'rgba(245,158,11,0.13)', border: '1px solid rgba(245,158,11,0.35)', '& .MuiChip-label': { px: 0.65 } }}
                            />
                          </Tooltip>
                        )}
                        {!req.removedFromPlaylistAt && req.keepInPlaylist && req.addedToPlaylistByApp && (req.status === 'PLAYED' || req.status === 'SKIPPED') && (
                          <Tooltip title="Kept in your playlist permanently" placement="top" arrow>
                            <Chip
                              label="Kept"
                              size="small"
                              sx={{ height: 22, fontSize: 11, fontWeight: 900, flexShrink: 0, color: SPOTIFY_GREEN, bgcolor: alpha(SPOTIFY_GREEN, 0.12), border: `1px solid ${alpha(SPOTIFY_GREEN, 0.35)}`, '& .MuiChip-label': { px: 0.65 } }}
                            />
                          </Tooltip>
                        )}
                        {req.status === 'REJECTED' && req.rejectionReason && (
                          <Tooltip title={`Rejected: ${req.rejectionReason}`} placement="top" arrow>
                            <IconButton
                              aria-label={`Rejection reason: ${req.rejectionReason}`}
                              size="small"
                              sx={{
                                width: 26,
                                height: 26,
                                flexShrink: 0,
                                color: '#F87171',
                                bgcolor: 'rgba(239,68,68,0.14)',
                                border: '1px solid rgba(248,113,113,0.42)',
                                '&:hover': { bgcolor: 'rgba(239,68,68,0.24)' },
                              }}
                            >
                              <AppIcon path={iconPaths.info} sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>

                    {req.guestNote && (
                      <Typography sx={{ mt: 0.65, fontSize: 12, color: alpha(theme.palette.common.white, 0.42), fontStyle: 'italic', lineHeight: 1.35 }}>
                        "{req.guestNote}"
                      </Typography>
                    )}
                  </Box>

                  <Stack
                    direction={{ xs: 'row', md: 'column' }}
                    spacing={{ xs: 0.8, md: 0.85 }}
                    alignItems={{ xs: 'center', md: 'flex-end' }}
                    sx={{ minWidth: 0 }}
                  >
                    {req.explicit && (
                      <Box sx={{ flexShrink: 0, alignSelf: { xs: 'center', md: 'flex-end' } }}>
                        {explicitBadge}
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>{requesterBlock}</Box>
                  </Stack>

                  {hasActionRail && (
                    <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 1 }}>
                      {actionRail}
                    </Box>
                  )}
                </Box>
              </Box>
              {hasActionRail && (
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                  {actionRail}
                </Box>
              )}
              </Box>
            );
          })}
          <Box ref={sentinelRef} sx={{ height: 1 }} />
          {isFetchingNextPage && (
            <Box sx={{ py: 1.5, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={18} sx={{ color: alpha(theme.palette.common.white, 0.25) }} />
            </Box>
          )}
        </Stack>
      )}
    </GlassCard>

    <Popover
      open={!!embedAnchor}
      anchorEl={embedAnchor}
      onClose={() => { setEmbedAnchor(null); setEmbedTrackId(null); }}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      slotProps={{ paper: { sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'visible' } } }}
    >
      {embedTrackId && (
        <Box component="iframe"
          src={`https://open.spotify.com/embed/track/${embedTrackId}?utm_source=generator&theme=0&autoplay=1`}
          width={300}
          height={80}
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
          sx={{ border: 'none', borderRadius: 1.5, display: 'block' }}
        />
      )}
    </Popover>
    </>
  );
}

// ── Loading skeleton ───────────────────────────────────────────────────────

function PageSkeleton() {
  const theme = useTheme();
  const sk = (w: string | number, h: number, r = 1) => (
    <Skeleton variant="rounded" width={w} height={h} sx={{ bgcolor: alpha(theme.palette.common.white, 0.07), borderRadius: r }} />
  );
  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        {sk(80, 14)}
        {sk('60%', 36, 1.5)}
        {sk('40%', 14)}
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 320px' }, gap: 2 }}>
        <Stack spacing={2}>{sk('100%', 320, 1.5)}</Stack>
        <Stack spacing={2}>{sk('100%', 180, 1.5)}{sk('100%', 140, 1.5)}</Stack>
      </Box>
    </Stack>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export function DjEventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const theme = useTheme();
  const navigate = useNavigate();

  const [event, setEvent] = useState<DjEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [reqStatus, setReqStatus] = useState<EventRequestStatus>('OPEN');
  const [spotifyPlaying, setSpotifyPlaying] = useState<boolean | null>(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!eventId) return;
    Promise.all([
      eventApi.getEvent(eventId),
      eventApi.getEventPlayback(eventId).catch(() => null),
    ])
      .then(([e, playback]) => {
        setEvent(e);
        setReqStatus(e.requestStatus);
        setSpotifyPlaying(playback?.isPlaying ?? e.isPlaying ?? null);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [eventId]);

  const handleRequestStatusChange = async (next: EventRequestStatus) => {
    if (!event) return;
    const prev = reqStatus;
    setReqStatus(next);
    setStatusUpdating(true);
    try {
      await eventApi.updateEvent(event.eventId, { requestStatus: next });
    } catch {
      setReqStatus(prev);
    } finally {
      setStatusUpdating(false);
    }
  };

  useEffect(() => {
    if (!eventId || event?.status !== 'ACTIVE') return;
    const timer = setInterval(() =>
      eventApi.getEventPlayback(eventId)
        .then(({ isPlaying }) => setSpotifyPlaying(isPlaying))
        .catch(() => {}),
      30_000,
    );
    return () => clearInterval(timer);
  }, [eventId, event?.status]);

  const handlePlaylistChange = async (id: string, name: string) => {
    if (!event) return;
    await eventApi.updateEvent(event.eventId, { spotifyPlaylistId: id, spotifyPlaylistName: name });
    setEvent((prev) => prev ? { ...prev, spotifyPlaylistId: id, spotifyPlaylistName: name } : prev);
  };

  const handleShareToggle = async (shared: boolean) => {
    if (!event) return;
    const prev = event.sharePlaylist;
    setEvent((e) => e ? { ...e, sharePlaylist: shared } : e);
    try {
      await eventApi.updateEvent(event.eventId, { sharePlaylist: shared });
    } catch {
      setEvent((e) => e ? { ...e, sharePlaylist: prev } : e);
    }
  };

  const handleDelete = async () => {
    if (!event) return;
    setDeleting(true);
    try {
      await eventApi.deleteEvent(event.eventId);
      navigate(routePaths.djEvents);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <DjPageShell><PageSkeleton /></DjPageShell>;

  if (notFound || !event) {
    return (
      <DjPageShell>
        <Stack alignItems="center" spacing={2} sx={{ pt: 8, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 22, fontWeight: 900, color: alpha(theme.palette.common.white, 0.6) }}>Event not found</Typography>
          <Typography sx={{ fontSize: 14, color: alpha(theme.palette.common.white, 0.35) }}>
            This event doesn't exist or you don't have access to it.
          </Typography>
          <Button onClick={() => navigate(routePaths.djEvents)} sx={{ color: alpha(theme.palette.common.white, 0.6), '&:hover': { color: 'common.white' } }}>
            ← Back to Events
          </Button>
        </Stack>
      </DjPageShell>
    );
  }

  const sm = statusMeta[event.status];
  const rm = event.status === 'ACTIVE' && spotifyPlaying === false
    ? { label: 'Not playing', dot: '#F59E0B' }
    : reqStatusMeta[reqStatus];

  return (
    <DjPageShell>
      <Stack spacing={3}>

        {/* Breadcrumb */}
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Button
            onClick={() => navigate(routePaths.djEvents)}
            startIcon={<AppIcon path={iconPaths.back} />}
            size="small"
            sx={{ color: alpha(theme.palette.common.white, 0.5), fontSize: 13, fontWeight: 700, '&:hover': { color: 'common.white', bgcolor: alpha(theme.palette.common.white, 0.08) } }}
          >
            Events
          </Button>
          <Typography sx={{ color: alpha(theme.palette.common.white, 0.22), fontSize: 14 }}>/</Typography>
          <Typography noWrap sx={{ color: alpha(theme.palette.common.white, 0.55), fontSize: 13, fontWeight: 700, maxWidth: 200 }}>
            {event.name}
          </Typography>
        </Stack>

        {/* Hero */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
          justifyContent="space-between"
          spacing={2}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2.25,
            border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
            bgcolor: alpha(theme.palette.common.white, 0.055),
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 58px ${alpha(theme.palette.common.black, 0.2)}`,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -80,
              right: -130,
              width: 300,
              height: 210,
              pointerEvents: 'none',
              background: `linear-gradient(115deg, transparent 0 24%, ${alpha(theme.palette.secondary.light, 0.2)} 24% 36%, transparent 36% 52%, ${alpha(theme.palette.warning.light, 0.15)} 52% 62%, transparent 62%)`,
              transform: 'skewY(-8deg)',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.success.light}, ${theme.palette.secondary.light}, ${theme.palette.warning.light})`,
              opacity: 0.75,
            },
          }}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ position: 'relative', mb: 1.25 }}>
              <Chip label={sm.label} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 800, color: sm.color, bgcolor: sm.bg, border: `1px solid ${sm.border}`, '& .MuiChip-label': { px: 0.875 } }} />
              <Stack direction="row" alignItems="center" spacing={0.6}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: rm.dot }} />
                <Typography sx={{ fontSize: 12, color: alpha(theme.palette.common.white, 0.45), fontWeight: 600 }}>{rm.label}</Typography>
              </Stack>
            </Stack>

            <Typography variant="h3" sx={{ position: 'relative', fontSize: { xs: 32, sm: 40, md: 48 }, fontWeight: 900, color: 'common.white', lineHeight: { xs: 0.98, sm: 1.03 }, mb: 1.35, textWrap: 'balance' }}>
              {event.name}
            </Typography>

            <Stack spacing={0.75} sx={{ position: 'relative' }}>
              {event.venueName && (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <AppIcon path={iconPaths.location} sx={{ fontSize: 15, color: alpha(theme.palette.secondary.light, 0.72) }} />
                  <Typography sx={{ fontSize: 14, color: alpha(theme.palette.common.white, 0.66), fontWeight: 650 }}>{event.venueName}</Typography>
                </Stack>
              )}
              {event.recurrence === 'weekly' && event.recurrenceDays?.length ? (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <AppIcon path={iconPaths.calendar} sx={{ fontSize: 15, color: alpha(theme.palette.warning.light, 0.72) }} />
                  <Typography sx={{ fontSize: 14, color: alpha(theme.palette.common.white, 0.62), lineHeight: 1.35 }}>
                    Every {formatRecurrenceDays(event.recurrenceDays)}
                    {event.startTime && (
                      <Box component="span" sx={{ ml: 1, color: alpha(theme.palette.common.white, 0.35) }}>
                        {formatEventTime(new Date().toISOString().slice(0, 10), event.startTime, event.endTime)}
                      </Box>
                    )}
                    {event.recurrenceEndDate && (
                      <Box component="span" sx={{ ml: 1, color: alpha(theme.palette.common.white, 0.28), fontSize: 12 }}>
                        · until {formatDate(event.recurrenceEndDate)}
                      </Box>
                    )}
                  </Typography>
                </Stack>
              ) : (event.eventDate || event.startTime) && (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <AppIcon path={iconPaths.calendar} sx={{ fontSize: 15, color: alpha(theme.palette.warning.light, 0.72) }} />
                  <Typography sx={{ fontSize: 14, color: alpha(theme.palette.common.white, 0.62), lineHeight: 1.35 }}>
                    {formatDate(event.eventDate)}
                    {event.startTime && (
                      <Box component="span" sx={{ ml: 1, color: alpha(theme.palette.common.white, 0.35) }}>
                        {formatEventTime(event.eventDate, event.startTime, event.endTime)}
                      </Box>
                    )}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Box>

          {event.status !== 'ARCHIVED' && (
            <Tooltip title="Archive event">
              <IconButton
                onClick={() => setDeleteOpen(true)}
                size="small"
                sx={{ position: 'relative', color: alpha(theme.palette.common.white, 0.38), border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`, borderRadius: 1.5, p: 1, bgcolor: alpha(theme.palette.common.white, 0.04), '&:hover': { color: theme.palette.error.light, bgcolor: alpha(theme.palette.error.main, 0.12), borderColor: alpha(theme.palette.error.main, 0.3) } }}
              >
                <AppIcon path={iconPaths.delete} sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        {/* Main layout */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 300px' }, gap: 2, alignItems: 'start' }}>

          {/* Left: request queue */}
          <RequestQueue
            event={event}
            requestStatus={reqStatus}
            onStatusChange={handleRequestStatusChange}
            statusUpdating={statusUpdating}
            spotifyPlaying={spotifyPlaying}
          />

          {/* Right: share + spotify */}
          <Stack spacing={2}>
            <QrPanel event={event} />
            <SpotifyPanel event={event} onNavigate={() => navigate(routePaths.djSpotify)} onPlaylistChange={handlePlaylistChange} onShareToggle={handleShareToggle} canEdit={event.status !== 'ACTIVE' || spotifyPlaying !== true || reqStatus === 'PAUSED'} />
          </Stack>

        </Box>
      </Stack>

      <ConfirmDialog
        open={deleteOpen}
        title={`Archive "${event.name}"?`}
        message="This will archive the event and close all pending requests. It will no longer appear in your active events."
        confirmText={deleting ? 'Archiving…' : 'Archive event'}
        cancelText="Keep it"
        onConfirm={handleDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </DjPageShell>
  );
}
